import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { loadApplication, saveDraftAccess } from "services/admissions";
import {
  PortalShell,
  Surface,
  Button,
  Field,
  TextInput,
  ErrorSummary,
  LangToggle,
  useT,
  useFontClass,
} from "components/admissions/portalUi";

/**
 * Continue an application on a device that has no saved draft — a parent who
 * started on a phone and came back on a shared computer.
 *
 * Number plus resume code, because there are no accounts. The code is the only
 * thing proving ownership, which is why the receipt tells families to keep it
 * and offers it for copy/download.
 */
export default function PortalContinue() {
  const t = useT();
  const font = useFontClass();
  const navigate = useNavigate();

  const [applicationNo, setApplicationNo] = useState("");
  const [resumeToken, setResumeToken] = useState("");
  const [errors, setErrors] = useState([]);
  const [busy, setBusy] = useState(false);

  const errFor = (id) => errors.find((e) => e.id === id)?.message;

  const onSubmit = async (e) => {
    e.preventDefault();
    const found = [];
    if (!applicationNo.trim())
      found.push({
        id: "applicationNo",
        message: t("আবেদন নম্বর লিখুন", "Enter your application number"),
      });
    if (!resumeToken.trim())
      found.push({ id: "resumeToken", message: t("রিজিউম কোড লিখুন", "Enter your resume code") });
    setErrors(found);
    if (found.length) return;

    setBusy(true);
    try {
      await loadApplication(applicationNo.trim(), resumeToken.trim());
      saveDraftAccess({ applicationNo: applicationNo.trim(), resumeToken: resumeToken.trim() });
      navigate("/admissions-portal/section");
    } catch (err) {
      // The API answers the same way for a wrong number and a wrong code, so
      // the message stays deliberately vague here too.
      setErrors([
        {
          id: "applicationNo",
          message: t(
            "এই নম্বর ও কোডে কোনো আবেদন পাওয়া যায়নি। আবার দেখে নিন।",
            "No application matches that number and code. Please check both."
          ),
        },
      ]);
    } finally {
      setBusy(false);
    }
  };

  return (
    <PortalShell>
      <div className="flex items-center justify-between gap-4 mb-6 flex-wrap">
        <Link
          to="/admissions-portal"
          className={`${font} text-[14px] text-muted no-underline hover:text-ink min-h-[44px] inline-flex items-center`}
        >
          ← {t("ফিরে যান", "Back")}
        </Link>
        <LangToggle />
      </div>

      <h1
        className={`${font} font-serif text-[28px] sm:text-[34px] font-bold text-green-dark leading-tight mb-2`}
      >
        {t("আবেদন চালিয়ে যান", "Continue your application")}
      </h1>
      <p className={`${font} text-[16px] text-muted leading-relaxed mb-6`}>
        {t(
          "শুরু করার সময় যে আবেদন নম্বর ও রিজিউম কোড দেওয়া হয়েছিল, সেটি লিখুন।",
          "Enter the application number and resume code you were given when you started."
        )}
      </p>

      <ErrorSummary errors={errors} className="mb-6" />

      <form onSubmit={onSubmit} noValidate>
        <Surface className="p-5 sm:p-7 mb-6">
          <div className="flex flex-col gap-5">
            <Field
              id="applicationNo"
              label={t("আবেদন নম্বর", "Application number")}
              hint={t("যেমন KCGS-2026-0001", "For example KCGS-2026-0001")}
              required
              error={errFor("applicationNo")}
            >
              <TextInput
                id="applicationNo"
                value={applicationNo}
                onChange={(e) => setApplicationNo(e.target.value)}
                error={errFor("applicationNo")}
                autoComplete="off"
              />
            </Field>
            <Field
              id="resumeToken"
              label={t("রিজিউম কোড", "Resume code")}
              required
              error={errFor("resumeToken")}
            >
              <TextInput
                id="resumeToken"
                value={resumeToken}
                onChange={(e) => setResumeToken(e.target.value)}
                error={errFor("resumeToken")}
                autoComplete="off"
              />
            </Field>
          </div>
        </Surface>

        <Button type="submit" disabled={busy} className="w-full sm:w-auto">
          {busy ? t("খোঁজা হচ্ছে…", "Looking…") : t("আবেদন খুলুন", "Open application")}
        </Button>

        <p className={`${font} text-[14px] text-muted mt-4 leading-relaxed`}>
          {t(
            "কোড হারিয়ে ফেললে বিদ্যালয় অফিসে যোগাযোগ করুন। ফোন ০২৪-৭৭৭২১৪২২।",
            "If you have lost your code, contact the school office on 024-77721422."
          )}
        </p>
      </form>
    </PortalShell>
  );
}
